import React, { useState, useEffect } from 'react';
import { 
  Building2, Users, Target, TrendingUp, ArrowUpRight, 
  Sparkles, CheckCircle2, ChevronRight, Award, PlusCircle, Mail,
  Wallet, Calendar, AlertCircle
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { bdAPI } from '../utils/api';

const BDDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({
    stats: {
      total_agencies: 0,
      active_agencies: 0,
      total_hosts: 0,
      target_amount: 1000000,
      achieved_amount: 0,
      progress_percentage: 0,
      current_month: ''
    },
    agencies: [],
    recent_hosts: [],
    chart_data: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await bdAPI.getDashboard();
      if (res.data?.success) {
        setData(res.data);
      }
    } catch (err) {
      console.error("Error fetching BD dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  const { stats, agencies, recent_hosts, chart_data } = data;

  if (loading) {
    return (
      <div className="space-y-8 animate-pulse">
        <div className="h-28 bg-white/5 rounded-[32px]"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-36 bg-white/5 rounded-[28px]"></div>
          ))}
        </div>
        <div className="h-80 bg-white/5 rounded-[32px]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Welcome Banner */}
      <div className="relative overflow-hidden bg-gradient-to-r from-emerald-950/60 via-slate-900 to-cyan-950/60 border border-emerald-500/20 rounded-[32px] p-8 md:p-10 shadow-2xl">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 blur-[100px] rounded-full"></div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-wider mb-3">
              <Sparkles size={14} />
              <span>Business Development Operations</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight">
              BD Executive Portal
            </h1>
            <p className="text-slate-400 mt-2 text-sm max-w-xl">
              Track and scale your assigned agencies, monitor live host performances, and maximize monthly coin circulation.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => navigate('/bd/agencies?action=invite')}
              className="px-5 py-3 rounded-2xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-sm shadow-lg shadow-purple-500/20 flex items-center gap-2 transition-all hover:scale-105 cursor-pointer"
            >
              <Mail size={18} />
              <span>Invite Agency</span>
            </button>
            <button
              onClick={() => navigate('/bd/agencies')}
              className="px-5 py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white font-bold text-sm shadow-lg shadow-emerald-500/20 flex items-center gap-2 transition-all hover:scale-105 cursor-pointer"
            >
              <PlusCircle size={18} />
              <span>Manage Agencies</span>
            </button>
            <button
              onClick={() => navigate('/bd/hosts')}
              className="px-5 py-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-sm flex items-center gap-2 transition-colors cursor-pointer"
            >
              <Users size={18} className="text-emerald-400" />
              <span>View Hosts</span>
            </button>
          </div>
        </div>
      </div>

      {/* BD Commission & Valid Days Highlight Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Card A: Pending Commission Diamonds */}
        <div className="bg-gradient-to-br from-amber-950/40 via-[#0b0f19] to-[#0b0f19] border border-amber-500/25 rounded-3xl p-6 relative overflow-hidden backdrop-blur-xl shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-500/25 flex items-center justify-center text-amber-400">
              <Sparkles size={24} />
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${
              stats.is_commission_unlocked 
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
            }`}>
              {stats.is_commission_unlocked ? 'Unlocked 🎉' : '10-Day Hold'}
            </span>
          </div>
          <h3 className="text-slate-400 text-xs font-bold uppercase tracking-wider">Pending BD Commission</h3>
          <p className="text-3xl font-black text-amber-400 mt-1 font-mono">
            {(stats.pending_diamonds || 0).toLocaleString()} <span className="text-lg">💎</span>
          </p>
          <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1.5">
            {stats.is_commission_unlocked ? (
              <span className="text-emerald-400 font-semibold flex items-center gap-1">
                <CheckCircle2 size={13} /> 10 Days Target Met — Released!
              </span>
            ) : (
              <span className="text-amber-300 font-semibold flex items-center gap-1">
                <AlertCircle size={13} /> {stats.days_remaining ?? Math.max(0, 10 - (stats.valid_days || 0))} more valid days needed to unlock
              </span>
            )}
          </p>
        </div>

        {/* Card B: BD Valid Days Progress */}
        <div className="bg-gradient-to-br from-emerald-950/40 via-[#0b0f19] to-[#0b0f19] border border-emerald-500/25 rounded-3xl p-6 relative overflow-hidden backdrop-blur-xl shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center text-emerald-400">
              <Calendar size={24} />
            </div>
            <span className="px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 font-bold text-xs border border-emerald-500/30">
              {stats.valid_days || 0} / 10 Days
            </span>
          </div>
          <h3 className="text-slate-400 text-xs font-bold uppercase tracking-wider">BD Active Days</h3>
          <div className="flex items-baseline gap-2 mt-1">
            <p className="text-3xl font-black text-white">{stats.valid_days || 0}</p>
            <span className="text-sm text-slate-400 font-bold">/ 10 Valid Days</span>
          </div>
          {/* Progress Bar */}
          <div className="w-full bg-white/5 rounded-full h-2 mt-3 overflow-hidden border border-white/10">
            <div 
              className={`h-full rounded-full transition-all duration-1000 ${
                stats.is_commission_unlocked 
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-400' 
                  : 'bg-gradient-to-r from-amber-500 to-emerald-500'
              }`}
              style={{ width: `${Math.min(100, Math.round(((stats.valid_days || 0) / 10) * 100))}%` }}
            ></div>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">
            Daily seat/mic session timer (2+ hrs daily)
          </p>
        </div>

        {/* Card C: BD Diamond Wallet */}
        <div className="bg-gradient-to-br from-teal-950/40 via-[#0b0f19] to-[#0b0f19] border border-teal-500/25 rounded-3xl p-6 relative overflow-hidden backdrop-blur-xl shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-2xl bg-teal-500/15 border border-teal-500/25 flex items-center justify-center text-teal-400">
              <Wallet size={24} />
            </div>
            <button 
              onClick={() => navigate('/bd/performance')}
              className="px-2.5 py-1 rounded-full bg-teal-500/10 text-teal-400 hover:bg-teal-500/20 text-xs font-bold transition flex items-center gap-1 cursor-pointer"
            >
              <span>Ledger</span>
              <ChevronRight size={12} />
            </button>
          </div>
          <h3 className="text-slate-400 text-xs font-bold uppercase tracking-wider">BD Wallet Balance</h3>
          <p className="text-3xl font-black text-teal-400 mt-1 font-mono">
            {(stats.diamond_balance || 0).toLocaleString()} <span className="text-lg">💎</span>
          </p>
          <p className="text-[11px] text-slate-400 mt-2">
            Credited: <span className="text-white font-semibold">{(stats.credited_diamonds || 0).toLocaleString()} 💎</span> released this month
          </p>
        </div>
      </div>

      {/* 4 Primary Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Card 1: Agencies */}
        <div className="bg-[#0f172a] border border-white/10 rounded-[28px] p-6 relative overflow-hidden group hover:border-emerald-500/40 transition-all duration-300">
          <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/10 blur-2xl group-hover:bg-emerald-500/20 transition-all"></div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">My Agencies</span>
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
              <Building2 size={22} />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-black text-white">{stats.total_agencies}</span>
            <span className="text-xs font-semibold text-emerald-400">({stats.active_agencies} Active)</span>
          </div>
          <p className="text-xs text-slate-400 mt-2">Agencies directly under your BD code</p>
        </div>

        {/* Card 2: Hosts */}
        <div className="bg-[#0f172a] border border-white/10 rounded-[28px] p-6 relative overflow-hidden group hover:border-cyan-500/40 transition-all duration-300">
          <div className="absolute top-0 right-0 w-24 h-24 bg-cyan-500/10 blur-2xl group-hover:bg-cyan-500/20 transition-all"></div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Agency Hosts</span>
            <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400">
              <Users size={22} />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-black text-white">{stats.total_hosts}</span>
            <span className="text-xs font-semibold text-cyan-400">Creators Active</span>
          </div>
          <p className="text-xs text-slate-400 mt-2">Hosts managed across your agencies</p>
        </div>

        {/* Card 3: Achieved Coins */}
        <div className="bg-[#0f172a] border border-white/10 rounded-[28px] p-6 relative overflow-hidden group hover:border-teal-500/40 transition-all duration-300">
          <div className="absolute top-0 right-0 w-24 h-24 bg-teal-500/10 blur-2xl group-hover:bg-teal-500/20 transition-all"></div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Month Coins Achieved</span>
            <div className="w-12 h-12 rounded-2xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400">
              <TrendingUp size={22} />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-black text-white">{stats.achieved_amount?.toLocaleString()}</span>
          </div>
          <p className="text-xs text-slate-400 mt-2">Combined coins generated this month</p>
        </div>
      </div>

      {/* Chart & Quick List Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Performance Overview Chart */}
        <div className="lg:col-span-2 bg-[#0f172a] border border-white/10 rounded-[32px] p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-white">Performance Trajectory</h3>
              <p className="text-xs text-slate-400 mt-1">Weekly coin volume & creator activity</p>
            </div>
            <div className="flex items-center gap-2 text-emerald-400 text-xs font-bold bg-emerald-500/10 px-3 py-1.5 rounded-xl border border-emerald-500/20">
              <ArrowUpRight size={16} />
              <span>Active Period</span>
            </div>
          </div>

          <div className="h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chart_data.length ? chart_data : [{ name: 'Today', coins: stats.achieved_amount }]}>
                <defs>
                  <linearGradient id="bdColorGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.35}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" vertical={false} />
                <XAxis dataKey="name" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', color: '#fff' }}
                />
                <Area type="monotone" dataKey="coins" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#bdColorGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Hosts across agencies */}
        <div className="bg-[#0f172a] border border-white/10 rounded-[32px] p-6 md:p-8 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Latest Agency Hosts</h3>
              <button
                onClick={() => navigate('/bd/hosts')}
                className="text-xs font-bold text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-1 cursor-pointer"
              >
                <span>View All</span>
                <ChevronRight size={14} />
              </button>
            </div>

            {recent_hosts.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Users size={36} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">No hosts registered yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recent_hosts.slice(0, 5).map((host) => (
                  <div key={host.id} className="flex items-center justify-between p-3 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-white/10 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-500/20 to-cyan-500/20 border border-white/10 flex items-center justify-center text-white font-bold text-sm">
                        {host.first_name ? host.first_name.charAt(0).toUpperCase() : 'H'}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-white leading-tight">
                          {host.first_name} {host.last_name}
                        </p>
                        <p className="text-[11px] text-slate-400">Agency: <span className="text-emerald-400 font-semibold">{host.agency_name}</span></p>
                      </div>
                    </div>
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                      Approved
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Managed Agencies Overview Table */}
      <div className="bg-[#0f172a] border border-white/10 rounded-[32px] p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-white">My Managed Agencies</h3>
            <p className="text-xs text-slate-400 mt-1">Direct performance summary of agencies assigned to your BD ID</p>
          </div>
          <button
            onClick={() => navigate('/bd/agencies')}
            className="text-xs font-bold text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-1 cursor-pointer"
          >
            <span>All Agencies</span>
            <ChevronRight size={14} />
          </button>
        </div>

        {agencies.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <Building2 size={42} className="mx-auto mb-3 opacity-30 text-emerald-400" />
            <p className="text-base font-semibold text-white">No Agencies Assigned Yet</p>
            <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
              Onboard your first agency to begin managing hosts and tracking performance.
            </p>
            <button
              onClick={() => navigate('/bd/agencies')}
              className="mt-4 px-5 py-2.5 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 text-xs font-bold transition-all cursor-pointer"
            >
              + Onboard Agency
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  <th className="pb-4">Agency</th>
                  <th className="pb-4">Code</th>
                  <th className="pb-4">Manager</th>
                  <th className="pb-4">Hosts</th>
                  <th className="pb-4">Month Coins</th>
                  <th className="pb-4">BD Commission</th>
                  <th className="pb-4">Status</th>
                  <th className="pb-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 text-sm">
                {agencies.map((agency) => (
                  <tr key={agency.id} className="hover:bg-white/[0.02] transition-colors">
                    <td className="py-4">
                      <div className="font-bold text-white">{agency.agency_name}</div>
                      <div className="text-xs text-slate-400">{agency.country || 'India'}</div>
                    </td>
                    <td className="py-4 font-mono font-bold text-emerald-400 text-xs">
                      #{agency.agency_code || '---'}
                    </td>
                    <td className="py-4 text-slate-300">
                      {agency.real_name || 'Agency Manager'}
                    </td>
                    <td className="py-4">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-white/5 border border-white/10 text-xs font-bold text-white">
                        <Users size={12} className="text-cyan-400" />
                        {agency.hosts_count}
                      </span>
                    </td>
                    <td className="py-4 font-bold text-white">
                      {agency.monthly_target_coins?.toLocaleString()}
                    </td>
                    <td className="py-4 font-bold text-amber-400 font-mono text-xs">
                      {(agency.total_bd_commission_diamonds || 0).toLocaleString()} 💎
                    </td>
                    <td className="py-4">
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                        {agency.status || 'Active'}
                      </span>
                    </td>
                    <td className="py-4 text-right">
                      <button
                        onClick={() => navigate('/bd/agencies')}
                        className="px-3 py-1.5 rounded-xl bg-white/5 hover:bg-emerald-500/20 text-xs font-semibold text-slate-300 hover:text-emerald-400 transition-colors cursor-pointer"
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BDDashboard;
